import type { Task } from "@app/core";
import { useEffect, useRef, useState, type MouseEvent } from "react";
import { useResizePreviewStore } from "../../stores/index.js";
import type { BlockDragVisual } from "./drag.js";

const RESIZE_STEP_MIN = 10;

interface Args {
  task: Task;
  hourHeight: number;
  /** Natural start (minutes from midnight) of the real occurrence. */
  startMinutes: number;
  /** Rendered height of THIS segment in minutes (nest-layout `heightMin`). For a
   *  multi-day timed task's overflow continuation this is the clamped per-day
   *  portion (e.g. 240 = ends 04:00), NOT the task's full `duration_minutes`. The
   *  bottom-resize preview grows from here so the block doesn't jump to the whole
   *  span's height while dragging. */
  segmentMinutes: number;
  /** Maps a cursor point to the grid instant under it (day column + time). When
   *  provided, the BOTTOM edge resizes ACROSS DAYS: drag the handle into a later
   *  column and the task grows to span those days (e.g. 2h → 45h). The grid paints
   *  a multi-column ghost from `useResizePreviewStore` while dragging. Absent (or
   *  for a dateless task) ⇒ same-column vertical resize only. */
  pointToInstant?: ((clientX: number, clientY: number) => Date | null) | undefined;
  /** Block look (wash vars / tone) for the cross-day ghost; captured at start. */
  visual?: BlockDragVisual | undefined;
  onResize: (t: Task, newDurationMinutes: number) => void;
  onResizeTop: (t: Task, newStartIso: string, newDurationMinutes: number) => void;
}

interface ResizeState {
  /** Previewed duration while dragging either edge (null = not resizing). */
  previewMinutes: number | null;
  /** Previewed start minute while dragging the TOP edge (null otherwise). */
  previewStartMin: number | null;
  /** True for the whole gesture AND a short settle tail after release — callers
   *  use it to keep layout animation OFF so the committed size snaps in without
   *  a spring bounce. */
  active: boolean;
  resizingRef: React.MutableRefObject<boolean>;
  startResize: (e: MouseEvent) => void;
  startResizeTop: (e: MouseEvent) => void;
}

/** Grace period (ms) after release during which layout stays un-animated so the
 *  freshly-committed size doesn't spring from a stale value. */
const SETTLE_MS = 160;

/** Bottom + top drag-resize behaviour for a calendar task block. */
export function useTaskResize({ task, hourHeight, startMinutes, segmentMinutes, pointToInstant, visual, onResize, onResizeTop }: Args): ResizeState {
  const [previewMinutes, setPreviewMinutes] = useState<number | null>(null);
  const [previewStartMin, setPreviewStartMin] = useState<number | null>(null);
  const [active, setActive] = useState(false);
  const resizingRef = useRef(false);
  const cleanupRef = useRef<(() => void) | null>(null);
  const settleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // True while THIS hook owns an active cross-day ghost, so a mid-drag unmount
  // clears it (rather than leaking a frozen ghost or clobbering another block's).
  const ghostActiveRef = useRef(false);
  useEffect(() => () => {
    cleanupRef.current?.();
    if (settleRef.current) clearTimeout(settleRef.current);
    if (ghostActiveRef.current) useResizePreviewStore.getState().setPreview(null);
  }, []);

  function dyToMinutes(dy: number): number {
    return Math.round((dy / hourHeight) * 60 / RESIZE_STEP_MIN) * RESIZE_STEP_MIN;
  }

  function begin(e: MouseEvent, onMove: (ev: globalThis.MouseEvent) => void, onUp: () => void) {
    e.stopPropagation();
    e.preventDefault();
    resizingRef.current = true;
    setActive(true);
    if (settleRef.current) { clearTimeout(settleRef.current); settleRef.current = null; }
    function cleanup() {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", finish);
      cleanupRef.current = null;
    }
    function finish() {
      cleanup();
      setPreviewMinutes(null);
      setPreviewStartMin(null);
      setTimeout(() => { resizingRef.current = false; }, 0);
      onUp();
      // Keep layout un-animated through the commit so the new size snaps in.
      settleRef.current = setTimeout(() => { setActive(false); settleRef.current = null; }, SETTLE_MS);
    }
    cleanupRef.current = cleanup;
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", finish);
  }

  function startResize(e: MouseEvent) {
    const startY = e.clientY;
    const startDur = task.duration_minutes;
    const startMs = task.due_at ? new Date(task.due_at).getTime() : null;
    // Cross-day path: map the cursor to a grid instant so the bottom edge can be
    // dragged into later day columns (the grid paints a spanning ghost from
    // `useResizePreviewStore`; the source block + its continuations dim). Falls
    // back to the same-column vertical preview when geometry/start is missing.
    const crossDay = !!pointToInstant && startMs != null && !!visual;
    const setGhost = useResizePreviewStore.getState().setPreview;
    // Preview grows from the SEGMENT's rendered height; the same-column commit
    // applies the matching delta to the FULL duration (they differ for a
    // multi-day task's overflow continuation, one clamped day's portion).
    const startSeg = Math.max(RESIZE_STEP_MIN, segmentMinutes);
    let latestDur = startDur;
    if (crossDay) { ghostActiveRef.current = true; setGhost({ taskId: task.id, startMs: startMs!, endMs: startMs! + startDur * 60_000, visual: visual! }); }
    else setPreviewMinutes(startSeg);
    begin(
      e,
      (ev) => {
        if (crossDay) {
          const end = pointToInstant!(ev.clientX, ev.clientY);
          if (!end) return;
          latestDur = Math.max(RESIZE_STEP_MIN, Math.round((end.getTime() - startMs!) / 60_000 / RESIZE_STEP_MIN) * RESIZE_STEP_MIN);
          setGhost({ taskId: task.id, startMs: startMs!, endMs: startMs! + latestDur * 60_000, visual: visual! });
          return;
        }
        // Clamp the delta so the previewed segment stays ≥ one step; apply that
        // same clamped delta to the full duration so preview and commit agree.
        const delta = Math.max(dyToMinutes(ev.clientY - startY), RESIZE_STEP_MIN - startSeg);
        latestDur = Math.max(RESIZE_STEP_MIN, startDur + delta);
        setPreviewMinutes(startSeg + delta);
      },
      () => {
        if (crossDay) { setGhost(null); ghostActiveRef.current = false; }
        if (latestDur !== startDur) onResize(task, latestDur);
      },
    );
  }

  function startResizeTop(e: MouseEvent) {
    if (!task.due_at) return;
    const startY = e.clientY;
    const startDur = task.duration_minutes;
    const maxDelta = startDur - RESIZE_STEP_MIN; // keep at least one step of duration
    let latestStart = startMinutes;
    setPreviewStartMin(startMinutes);
    setPreviewMinutes(startDur);
    begin(
      e,
      (ev) => {
        const shift = Math.min(Math.max(dyToMinutes(ev.clientY - startY), -startMinutes), maxDelta);
        latestStart = startMinutes + shift;
        setPreviewStartMin(latestStart);
        setPreviewMinutes(startDur - shift);
      },
      () => {
        if (latestStart === startMinutes) return;
        const d = new Date(task.due_at!);
        d.setHours(Math.floor(latestStart / 60), latestStart % 60, 0, 0);
        onResizeTop(task, d.toISOString(), startDur - (latestStart - startMinutes));
      },
    );
  }

  return { previewMinutes, previewStartMin, active, resizingRef, startResize, startResizeTop };
}
